import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  APIProvider,
  Map,
  Marker,
} from "@vis.gl/react-google-maps";
import { InputGroup, Form, Table, Card } from "react-bootstrap";
import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import styles from "./Establecimientos.module.css";
import { Establecimiento } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";

function normalizeText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function matchesSearchTerm(nombre: string, searchTerm: string): boolean {
  const normalizedNombre = normalizeText(nombre);
  const searchTerms = normalizeText(searchTerm).split(" ");

  return searchTerms.every(term => normalizedNombre.includes(term));
}

function Establecimientos() {
  const { setLoading } = useLoading();
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [selectedEstablecimiento, setSelectedEstablecimiento] = useState<Establecimiento | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [zoomLevel, setZoomLevel] = useState<number>(3.5);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const establecimientoId = params.get("establecimientoId");
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  const filteredEstablecimientos = establecimientos.filter(establecimiento =>
    matchesSearchTerm(establecimiento.nombre, searchTerm)
  );

  const handleEstablecimientoSelect = (establecimiento: Establecimiento) => {
    fetchEstablecimientoById(establecimiento.id.toString());
    setSearchTerm("");
    setZoomLevel(15);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleMarkerClick = (establecimiento: Establecimiento) => {
    fetchEstablecimientoById(establecimiento.id.toString());
    setZoomLevel(15);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/establecimientos/fullEstablecimientos`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Error al cargar los datos de establecimientos");
        }
        const data: Establecimiento[] = await response.json();
        setEstablecimientos(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

    const fetchEstablecimientoById = async (id: string | null) => {
      setLoading(true);

      if (!id) {
        setSelectedEstablecimiento(null);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/establecimientos/establecimiento/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Error al cargar el establecimiento");
        }
        const data: Establecimiento = await response.json();
        setSelectedEstablecimiento(data);
      } catch (error) {
        console.error("Error fetching establecimiento:", error);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchEstablecimientoById(establecimientoId);
  }, [establecimientoId, setLoading]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        clearInterval(interval);
      }
    }, 500);
  
    return () => clearInterval(interval);
  }, []);  

  const positionArg = { lat: -38.4161, lng: -63.6167 };
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;

  if (!apiKey) {
    throw new Error("No se encontraron las claves de la API de Google Maps en las variables de entorno.");
  }

  const mapStyles = [
    {
      featureType: "all",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }]
    }
  ];

  return (
    <>
      <Header />
      <NavBar />
      <div className={styles.tableContainer}>
        <h2 className={styles.titlesRoutes}>Establecimientos</h2>
        <div className="d-flex justify-content-between align-items-center">
          <InputGroup className={styles.searchBarWidth}>
            <InputGroup.Text>
              <i className="pi pi-search" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar"
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
          </InputGroup>
        </div>
        {searchTerm && (
          <Table striped bordered hover className={styles.organigramTable}>
            <tbody className={styles.organigramTbody}>
              {filteredEstablecimientos.length > 0 ? (
                filteredEstablecimientos.map((establecimiento, index) => (
                  <tr
                    key={index}
                    onClick={() => handleEstablecimientoSelect(establecimiento)}
                    className={styles.searchTable}
                  >
                    <td className={styles.organigramTableTd}>
                      <div>{establecimiento.nombre}</div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className={styles.organigramTableTd}>
                    No se encontraron resultados para "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        <Card className={`mb-2 ${styles.card}`}>
          <section className={styles.selectedOrgSection}>
            <div className={styles.selectedOrgContainer}>
            <APIProvider apiKey={apiKey}>
              <div className={styles.mapContainer}>
                <Map
                  defaultCenter={positionArg}
                  center={
                    selectedEstablecimiento
                      ? { lat: parseFloat(selectedEstablecimiento.lat), lng: parseFloat(selectedEstablecimiento.lng) }
                      : positionArg
                  }
                  zoom={selectedEstablecimiento ? 16 : 3.5}
                  className={styles.mapContainer}
                  mapTypeId="roadmap"
                  mapTypeControl={true}
                  scrollwheel={false}
                  disableDoubleClickZoom={true}
                  gestureHandling="greedy"
                  keyboardShortcuts={false}
                  zoomControl={false}
                  styles={mapStyles}
                >
                    {googleMapsLoaded && filteredEstablecimientos.length > 0 && filteredEstablecimientos.map(establecimiento => {
                      const isSelected = selectedEstablecimiento && establecimiento.id === selectedEstablecimiento.id;
                      return (
                        <Marker
                          key={establecimiento.id}
                          position={{ lat: parseFloat(establecimiento.lat), lng: parseFloat(establecimiento.lng) }}
                          onClick={() => handleMarkerClick(establecimiento)}
                          icon={{
                            url: isSelected
                              ? "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                              : "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                            scaledSize: typeof window !== "undefined" && window.google && window.google.maps
                              ? new window.google.maps.Size(32, 32)
                              : undefined,
                          }}
                        />
                      );
                    })}
                </Map>
              </div>
            </APIProvider>
            </div>
          </section>

          {selectedEstablecimiento && (
            <section className={styles.selectedFuncionarioSection}>
              <div className={styles.selectedFuncionarioContainer}>
                <ul className={styles.selectedFuncionarioHeader}>
                  <li className={styles.selectedFuncionarioHeaderContent}>
                    <p className={styles.selectedFuncionarioName}>
                      {selectedEstablecimiento.nombre}
                    </p>
                  </li>
                </ul>
                <div className={styles.selectedFuncionarioGde}>
                  <div className={styles.containerGde}>
                    <p>
                      {selectedEstablecimiento.domicilio_calle} {selectedEstablecimiento.domicilio_numero}
                    </p>
                  </div>
                </div>
                {selectedEstablecimiento.domicilio_referencia && (
                  <div className={styles.selectedFuncionarioUbi}>
                    <p>{selectedEstablecimiento.domicilio_referencia}</p>
                  </div>
                )}
                <div className={styles.selectedFuncionarioContact}>
                  <p>
                    CP: {selectedEstablecimiento.domicilio_cp ? selectedEstablecimiento.domicilio_cp : "Sin datos"} - {" "}
                    {selectedEstablecimiento.localidad_nombre ? `${selectedEstablecimiento.localidad_nombre}, ` : ""}
                    {selectedEstablecimiento.provincia_nombre}
                  </p>
                </div>
              </div>
            </section>
          )}
        </Card>
      </div>
      <Footer />
    </>
  );
}

export default Establecimientos;
